import { supabase } from '../lib/supabase'
import type { StaffRole } from './staffAuth'

/**
 * PORTAL STAFF TEAM — invitations and the staff list.
 *
 * Both go through the staff team Supabase Edge Function, never directly to
 * Supabase from the browser. The function holds the service-role credentials
 * server-side and re-checks, on every request, that the caller is an owner in
 * staff_users. There is deliberately no admin API call and no service-role key
 * anywhere in this file — or anywhere else in the front end.
 */

export interface StaffMember {
  userId: string
  name: string
  email: string
  role: StaffRole
  /** True until the invited person has set their password and signed in. */
  pending: boolean
}

interface TeamResult {
  ok: boolean
  error?: string
  members?: StaffMember[]
}

interface InviteResult {
  ok: boolean
  error?: string
}

const UNAVAILABLE = 'Team management is unavailable. Try again shortly.'

/* The deployed endpoint slug. Its source lives at
   supabase/functions/staff-team/index.ts, but Supabase assigned the deployed
   function this name — change it here only if the deployment is renamed. */
const STAFF_TEAM_FUNCTION = 'smooth-service'

function member(row: Record<string, unknown>): StaffMember {
  const role = row.role === 'owner' ? 'owner' : 'staff'
  return {
    userId: String(row.user_id ?? ''),
    name: String(row.name ?? '') || String(row.email ?? ''),
    email: String(row.email ?? ''),
    role,
    pending: Boolean(row.pending),
  }
}

/** The authorised staff list, returned by the function to owners only. */
export async function fetchStaffTeam(): Promise<TeamResult> {
  if (!supabase) return { ok: false, error: UNAVAILABLE }

  const { data, error } = await supabase.functions.invoke(STAFF_TEAM_FUNCTION, {
    body: { action: 'list' },
  })

  if (error) {
    console.error('[supabase] staff team list failed', error)
    return { ok: false, error: UNAVAILABLE }
  }
  if (data?.error) return { ok: false, error: String(data.error) }

  const rows = Array.isArray(data?.members) ? data.members : []
  return { ok: true, members: rows.map(member) }
}

/**
 * Invites a new Portal staff member. The function sends the Supabase Auth
 * invitation and writes the staff_users row with the chosen role; the browser
 * only passes the address, the role, and where the invitation should land.
 */
export async function inviteStaffMember(
  email: string,
  role: StaffRole,
): Promise<InviteResult> {
  if (!supabase) return { ok: false, error: UNAVAILABLE }

  const address = email.trim().toLowerCase()
  if (!address || !address.includes('@')) {
    return { ok: false, error: 'Enter a valid email address.' }
  }

  const { data, error } = await supabase.functions.invoke(STAFF_TEAM_FUNCTION, {
    body: {
      action: 'invite',
      email: address,
      role,
      redirectTo: `${window.location.origin}/staff/setup-password`,
    },
  })

  if (error) {
    console.error('[supabase] staff team invite failed', error)
    return { ok: false, error: 'Invitation could not be sent.' }
  }
  if (data?.error) return { ok: false, error: String(data.error) }

  return { ok: true }
}
